import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Eye, Users, Clock, Globe, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const AXIS_STYLE = { fontSize: 11, fill: "#64748B" };
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #CBD5E1", fontSize: 12 };

export default function VisitStatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/visit-stats");
      setStats(data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Visit stats error:", e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#64748B]" /></div>;
  if (!stats) return null;

  return (
    <div className="space-y-4" data-testid="visit-stats-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={<Clock className="w-5 h-5 text-[#EF4444]" />} label="En ligne maintenant" value={stats.realtime_5min} accent />
        <StatCard icon={<Users className="w-5 h-5 text-[#10B981]" />} label="Visiteurs uniques aujourd'hui" value={stats.unique_today} />
        <StatCard icon={<Eye className="w-5 h-5 text-[#3B82F6]" />} label="Pages vues aujourd'hui" value={stats.today} />
        <StatCard icon={<Globe className="w-5 h-5 text-[#FACC15]" />} label="Total pages vues" value={stats.total} />
      </div>

      {stats.daily?.length > 0 && (
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-5">
          <h3 className="text-sm font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit' }}>Visiteurs uniques - 7 derniers jours</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.daily}>
              <XAxis dataKey="date" tick={AXIS_STYLE} tickFormatter={d => d?.slice(5) || d} />
              <YAxis tick={AXIS_STYLE} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [v, "Visiteurs uniques"]} />
              <Bar dataKey="count" fill="#0A2540" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "bg-[#EF4444]/5 border-[#EF4444]/20" : "bg-white border-[#CBD5E1]"}`}>
      <div className="flex items-center gap-2 mb-2">{icon}<p className="text-xs text-[#64748B]">{label}</p></div>
      <p className={`text-2xl font-bold ${accent ? "text-[#EF4444]" : "text-[#0A2540]"}`} style={{ fontFamily: 'Outfit' }}>{value?.toLocaleString() || 0}</p>
    </div>
  );
}
